import { API_URL } from '../config/apiConfig';
import { buildOrderDetailLangQuery } from './buildOrderDetailLangQuery';

/**
 * Overlay translated name/description from GET /order/:id/full (same path as Barracks rewards).
 * @param {object[]} orders
 * @param {string | undefined} i18nLanguage
 * @param {string} token
 * @returns {Promise<object[]>}
 */
export async function enrichOrdersWithLocalizedNames(orders, i18nLanguage, token) {
  if (!token) {
    throw new Error('token is required to load localized order names');
  }
  if (!Array.isArray(orders)) {
    throw new Error('orders is required to load localized order names');
  }
  if (orders.length === 0) {
    return orders;
  }

  const uniqueIds = [...new Set(orders.map((order) => order.id ?? order.order_id))];
  uniqueIds.forEach((id) => {
    if (id == null) {
      throw new Error('Order id is required to load localized order names');
    }
  });

  const byId = {};
  await Promise.all(
    uniqueIds.map(async (id) => {
      const res = await fetch(`${API_URL}/order/${id}/full?${buildOrderDetailLangQuery(i18nLanguage)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`Failed to load localized name for order ${id} (HTTP ${res.status})`);
      }
      const full = await res.json();
      if (!full?.name) {
        throw new Error(`Localized name missing for order ${id}`);
      }
      byId[id] = full;
    })
  );

  return orders.map((order) => {
    const id = order.id ?? order.order_id;
    const full = byId[id];
    if (!full) {
      throw new Error(`Localized order data missing for order ${id}`);
    }
    return {
      ...order,
      name: full.name,
      description: full.description,
    };
  });
}

/**
 * Overlay translated name/description onto Hall of Ascension unit payloads.
 * @param {object[]} units
 * @param {string | undefined} i18nLanguage
 * @param {string} token
 * @returns {Promise<object[]>}
 */
export async function enrichHallUnitsWithLocalizedNames(units, i18nLanguage, token) {
  if (!Array.isArray(units)) {
    throw new Error('units is required to load localized Hall of Ascension names');
  }
  if (units.length === 0) {
    return units;
  }
  const orders = units.map((unit) => {
    const orderId = unit.orderId ?? unit.order?.id ?? unit.order?.order_id;
    if (orderId == null) {
      throw new Error('Hall unit orderId is required to load localized names');
    }
    return {
      ...(unit.order || {}),
      id: orderId,
    };
  });
  const localizedOrders = await enrichOrdersWithLocalizedNames(orders, i18nLanguage, token);
  return units.map((unit, index) => {
    const localized = localizedOrders[index];
    if (!localized?.name) {
      throw new Error(`Localized name missing for Hall unit ${unit.orderId}`);
    }
    return {
      ...unit,
      name: localized.name,
      order: {
        ...(unit.order || {}),
        name: localized.name,
        description: localized.description,
      },
    };
  });
}
